import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { LockOpen, UserPlus } from 'lucide-react';
import { Skeleton } from '@shared/ui/feedback/Skeleton';
import { MarqueeText } from '@shared/ui/primitives/MarqueeText';
import { fetchAccessGateEvents, fetchAccessInviteLocations } from '@features/access-invites/owner/api';
import { NewPinStepsSheet } from '@features/access-invites/owner/NewPinStepsSheet';
import { GATE_EVENTS_QUERY_KEY, todayRows, type State } from '@features/access-invites/owner/visitor-access/TodayCard';
import { useActivePins } from '@features/access-invites/owner/visitor-access/useActivePins';
import { VisitSheet, type VisitTarget } from '@features/access-invites/owner/visitor-access/VisitSheet';
import { WidgetCard } from './WidgetCard';
import { widgetTitle, type WidgetProps } from './registry';

const WIDGET_VISITOR_LIMIT = 3;

/** Pill per visitor state, in the same tones as the Helpdesk pills. */
const STATE_PILL: Record<State, { label: string; bg: string; ink: string }> = {
  on_site: {
    label: 'On site',
    bg: 'color-mix(in oklab, var(--success) 14%, var(--surface-card))',
    ink: 'color-mix(in oklab, var(--success) 75%, var(--ink-primary))',
  },
  expected: {
    label: 'Expected',
    bg: 'color-mix(in oklab, var(--info) 14%, var(--surface-card))',
    ink: 'color-mix(in oklab, var(--info) 80%, var(--ink-primary))',
  },
  left: { label: 'Left', bg: 'var(--surface-sunken)', ink: 'var(--ink-secondary)' },
};

/**
 * Home widget: today's visitors (on site, expected, left; up to three) with New PIN and Open gate
 * side by side under them. A row opens that visit, the rest of the card opens Visitor access.
 * Home only shows it where access management is licensed (`requiresModule`).
 */
export function AccessManagementWidget({ config }: WidgetProps): JSX.Element {
  const navigate = useNavigate();

  const pins = useActivePins();
  const eventsQuery = useQuery({ queryKey: GATE_EVENTS_QUERY_KEY, queryFn: () => fetchAccessGateEvents() });
  const locationsQuery = useQuery({ queryKey: ['access-invite-locations'], queryFn: fetchAccessInviteLocations });

  const [newPinOpen, setNewPinOpen] = useState(false);
  // The visit stays set while its drawer slides away, so the drawer never empties mid-close.
  const [visit, setVisit] = useState<VisitTarget | null>(null);
  const [visitOpen, setVisitOpen] = useState(false);

  // 1. Today's rows, as Visitor access shows them: on site first, then expected, then left.
  const rows = todayRows(pins.yourPins, pins.guardPins, eventsQuery.data ?? []).slice(0, WIDGET_VISITOR_LIMIT);
  const loading = pins.isLoading || eventsQuery.isLoading;

  // 2. Only a property on a gate that opens over Bluetooth gets Open gate, as on Visitor access.
  const hasGate = Boolean(locationsQuery.data?.some((location) => location.features));

  return (
    <>
      <WidgetCard
        title={widgetTitle(config)}
        onClick={() => navigate('/app/access-invites')}
        className="gap-0 p-0"
      >
        {loading ? (
          <Skeleton className="m-4 h-12 rounded-lg" />
        ) : pins.isError || eventsQuery.isError ? (
          <div className="flex items-center gap-3 px-4 py-3.5">
            <p className="flex-1 text-body-sm text-ink-muted">Couldn't load today's visitors.</p>
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                void pins.refetch();
                void eventsQuery.refetch();
              }}
              className="shrink-0 py-1.5 text-body-sm font-semibold text-primary-700"
            >
              Try again
            </button>
          </div>
        ) : rows.length === 0 ? (
          <p className="px-4 py-3.5 text-body-sm text-ink-muted">No visitors today.</p>
        ) : (
          <ul className="divide-y divide-hairline">
            {rows.map((row) => {
              const pill = STATE_PILL[row.state];
              return (
                <li key={row.key}>
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      if ('groupId' in row.open) {
                        navigate(`/app/access-invites/groups/${row.open.groupId}`);
                      } else {
                        setVisit(row.open);
                        setVisitOpen(true);
                      }
                    }}
                    className="flex w-full items-center gap-3 px-4 py-3 text-left active:opacity-80"
                  >
                    <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-pill bg-sunken text-[0.9375rem] font-semibold text-primary-700">
                      {row.name.charAt(0).toUpperCase()}
                    </span>

                    <span className="min-w-0 flex-1">
                      <MarqueeText className="text-[0.9375rem] font-semibold text-ink">{row.name}</MarqueeText>
                      <MarqueeText className="text-[0.8125rem] text-ink-secondary">{row.line}</MarqueeText>
                    </span>

                    <span
                      className="shrink-0 rounded-pill px-2.5 py-0.5 text-[0.8125rem] font-medium"
                      style={{ backgroundColor: pill.bg, color: pill.ink }}
                    >
                      {pill.label}
                    </span>
                  </button>
                </li>
              );
            })}
          </ul>
        )}

        {/* 3. The two things members do every day, side by side. */}
        <div className={`grid gap-2 border-t border-hairline px-4 pb-3.5 pt-3 ${hasGate ? 'grid-cols-2' : 'grid-cols-1'}`}>
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              setNewPinOpen(true);
            }}
            className="flex h-11 items-center justify-center gap-1.5 rounded-pill bg-sunken text-body-sm font-semibold text-ink active:scale-[0.98]"
          >
            <UserPlus className="h-[1.0625rem] w-[1.0625rem]" strokeWidth={2} />
            New PIN
          </button>
          {hasGate ? (
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                navigate('/app/access-invites/gate');
              }}
              className="flex h-11 items-center justify-center gap-1.5 rounded-pill bg-primary-600 text-body-sm font-semibold text-primary-ink active:scale-[0.98]"
            >
              <LockOpen className="h-[1.0625rem] w-[1.0625rem]" strokeWidth={2} />
              Open gate
            </button>
          ) : null}
        </div>
      </WidgetCard>

      <VisitSheet target={visit} open={visitOpen} onOpenChange={setVisitOpen} />

      {/* A new PIN shows on Visitor access › Active PINs, as it does from there. */}
      <NewPinStepsSheet
        open={newPinOpen}
        onOpenChange={setNewPinOpen}
        onDone={() => {
          setNewPinOpen(false);
          navigate('/app/access-invites?tab=pins');
        }}
      />
    </>
  );
}
